import { BlockType, PageType, SectionElementType } from '@common/entities';

import { Block, Field } from 'types/fields';
import { AnyPage } from 'types/page';

export const useFilterPagesAndFields = () => {
  // Type guard to check if an element is a Block
  const isBlock = (element: Block | Field): element is Block => {
    return element.elementType === SectionElementType.Block && 'customType' in element;
  };

  const hidePagesAndFields = (
    pages: AnyPage[],
    hideObj: {
      hidePageTypes?: PageType[];
      hideBlockTypes?: BlockType[];
    },
  ): AnyPage[] => {
    const { hidePageTypes = [], hideBlockTypes = [] } = hideObj;

    const removePageTypes = new Set(hidePageTypes);
    const removeBlockTypes = new Set(hideBlockTypes);

    // Early exit if nothing to hide
    if (removePageTypes.size === 0 && removeBlockTypes.size === 0) return pages;

    return (
      pages
        .map((page) => {
          if (removeBlockTypes.size === 0 || page.type !== PageType.Blank || !page.sections) {
            return page;
          }

          const filteredSections = page.sections
            .map((section) => {
              if (!section.elements || section.elements.length === 0) return section;

              // Filter out specified block types
              const filteredElements = section.elements.filter((element: Block | Field) => {
                if (!isBlock(element)) return true;

                // Remove block if its type is in the removeBlockTypes set
                if (removeBlockTypes.has(element.customType)) return false;

                return true;
              });

              return {
                ...section,
                elements: filteredElements,
              };
            })
            // Remove sections with no elements
            .filter((section) => section.elements && section.elements.length > 0);

          return {
            ...page,
            sections: filteredSections,
          };
        })
        // Filter out pages based on criteria
        .filter((page) => {
          // 1. Remove if page type is in removePageTypes
          if (removePageTypes.has(page.type)) return false;

          // 2. Remove blank pages with no sections (after block filtering)
          if (page.type === PageType.Blank) {
            return page.sections && page.sections.length > 0;
          }

          return true;
        })
    );
  };

  return {
    hidePagesAndFields,
  };
};
